import {
  createContext,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { authApi } from "../api/auth";

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

// The admin session now lives in the httpOnly adminAccessToken cookie (CHO-1473),
// so the client no longer stores or decodes the JWT. The profile comes from the
// server (/admin/get-admin-user) via authApi.me().
const toUser = (profile) => ({
  ...profile,
  id: profile.id,
  userId: profile.id,
  permissions: Array.isArray(profile.permissions) ? profile.permissions : [],
});

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state, action) => {
  const handler = handlers[action.type];
  return handler ? handler(state, action) : state;
};

const AuthContext = createContext({
  ...initialState,
  platform: "JWT",
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

export function AuthProvider(props) {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        // Cookie-authenticated: ask the server who we are. A 401 means no valid
        // session, which is a normal unauthenticated state, not an error.
        const profile = await authApi.me();
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: true,
            user: toUser(profile),
          },
        });
      } catch (err) {
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await authApi.login({ email, password });

    // If MFA is enabled, defer logging in until OTP is verified
    if (response && response.is_mfa_enabled && !response.token) {
      return { mfaRequired: true };
    }

    if (!response || !response.token) {
      throw new Error("Invalid login response");
    }

    // The access token is now set as an httpOnly cookie by the login response;
    // load the profile from the server instead of decoding the JWT client-side.
    const profile = await authApi.me();
    dispatch({
      type: "LOGIN",
      payload: {
        user: toUser(profile),
      },
    });
    return { mfaRequired: false };
  }, []);

  const verifyOtpAndLogin = useCallback(async (email, code) => {
    const response = await authApi.verifyOtp({ email, code });
    if (!response || !response.access_token) {
      throw new Error("Invalid OTP verification response");
    }

    // Cookie is set by the verify-otp response; load the profile from the server.
    const profile = await authApi.me();
    dispatch({
      type: "LOGIN",
      payload: {
        user: toUser(profile),
      },
    });
  }, []);

  const logout = async () => {
    // Clear the httpOnly cookie server-side, then drop any legacy token + reload.
    try {
      await authApi.logout();
    } catch (err) {
      console.error(err);
    }
    window.localStorage.removeItem("accessToken");
    window.location.reload();
  };

  const contextValue = useMemo(
    () => ({
      ...state,
      platform: "JWT",
      login,
      verifyOtpAndLogin,
      logout,
    }),
    [state, login, verifyOtpAndLogin, logout]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
